/**
 * Display formatting shared by the pages.
 *
 * Nothing here reaches for a date library directly: event times are naive local
 * strings (`2026-10-01T14:00`) and the helpers keep them that way, which is what
 * the calendar export and the timeline both need.
 */

import { clsx, type ClassValue } from 'clsx'
import type { TFunction } from 'i18next'
import { twMerge } from 'tailwind-merge'

import { ACTIVITIES, type ActivityKey } from '@shared/activities'
import { CITIES, type CityKey } from '@shared/cities'
import type { Locale } from '@shared/locales'
import { TRAVEL_MODE_DEFINITIONS, type TravelMode } from '@shared/travel'

/**
 * Joins class names and resolves conflicting Tailwind utilities.
 *
 * `twMerge` is what makes conditional variants safe: a caller passing
 * `className="p-4"` overrides a component's default `p-2` instead of fighting it.
 */
export function cn(...values: ClassValue[]): string {
  return twMerge(clsx(values))
}

/**
 * Joins a list of names with the locale's own separator.
 *
 * The separator is catalog copy (`common.listSeparator`, `、` in Chinese and
 * `, ` in English) rather than a literal: an English sentence must not join
 * names with the Chinese `、`.
 */
export function joinList(values: readonly string[], t: TFunction): string {
  return values.join(t('common.listSeparator'))
}

/** Calendar date, `2026-10-01`. */
const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/

/** `2026-10-01T14:00` to a locale aware date + time string. */
export function formatLocalDateTime(value: string, locale: Locale): string {
  const [date, time] = value.split('T')
  if (!date || !time) {
    return value
  }
  const [year, month, day] = date.split('-') as [string, string, string]
  const formatted = locale === 'zh-CN' ? `${year}年${Number(month)}月${Number(day)}日` : `${date}`
  return `${formatted} ${time}`
}

/** `2026-10-01T14:00` or `2026-10-01` to a short form; anything else is unchanged. */
export function formatLocalShort(value: string, locale: Locale): string {
  const [date, time] = value.split('T')
  if (!date || !ISO_DATE_PATTERN.test(date)) {
    return value
  }
  const [, month, day] = date.split('-') as [string, string, string]
  const short = locale === 'zh-CN' ? `${Number(month)}/${Number(day)}` : date
  return time ? `${short} ${time}` : short
}

/** Unix seconds to a locale aware timestamp. */
export function formatUnixSeconds(seconds: number, locale: Locale): string {
  const date = new Date(seconds * 1000)
  return new Intl.DateTimeFormat(locale, {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: CITIES.shanghai.timeZone,
  }).format(date)
}

/**
 * Unix seconds to the calendar date of the event city, `YYYY-MM-DD`.
 *
 * The instant is projected into the city's zone before the date is taken: a
 * timestamp rendered with `Intl` is a localized string, not an ISO one, so slicing
 * it produces half a date.
 */
export function formatUnixDate(seconds: number): string {
  return localDateTimeInCity(new Date(seconds * 1000), CITIES.shanghai.timeZone).slice(0, 10)
}

/**
 * Wall clock of an instant in one zone, in the event's own naive format.
 *
 * Event times carry no offset, so every comparison a page makes against "now" has
 * to read the clock of the city the event happens in rather than the browser's.
 */
export function localDateTimeInCity(instant: Date, timeZone: string): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).formatToParts(instant)
  const read = (type: string): string => parts.find((part) => part.type === type)?.value ?? '00'
  return `${read('year')}-${read('month')}-${read('day')}T${read('hour')}:${read('minute')}`
}

/** Current wall clock in the city the product runs in, in the event's own format. */
export function nowLocalDateTime(now: Date = new Date()): string {
  return localDateTimeInCity(now, CITIES.shanghai.timeZone)
}

/**
 * Rounds a score for display without inventing precision.
 *
 * A score the engine never produced (a missing dimension, an overflow) is not a
 * number the reader can act on, so it renders as the shared placeholder instead
 * of `NaN`.
 */
export function formatScore(score: number): string {
  if (!Number.isFinite(score)) {
    return '—'
  }
  return Number.isInteger(score) ? String(score) : score.toFixed(1)
}

/** Percentage from a 0..1 probability. */
export function formatProbability(value: number | null): string | null {
  if (value === null || !Number.isFinite(value)) {
    return null
  }
  return `${Math.round(value * 100)}%`
}

/** Translation key for an activity. */
export function activityLabelKey(activity: ActivityKey): string {
  return ACTIVITIES[activity].labelKey
}

/** Translation key for a travel mode. */
export function modeLabelKey(mode: TravelMode): string {
  return TRAVEL_MODE_DEFINITIONS[mode].labelKey
}

/** Translation key for a city. */
export function cityLabelKey(city: CityKey): string {
  return CITIES[city]?.labelKey ?? 'city.shanghai'
}

/**
 * Formats a duration in minutes as `2 小时 30 分钟`, or just minutes.
 *
 * The total is rounded once, before it is split into hours and minutes:
 * rounding the remainder on its own reports 119.6 minutes as `1 小时 60 分钟`.
 * A duration the engine cannot have produced (non-finite) keeps the shared
 * placeholder, and a negative one has no display form other than zero.
 */
export function formatDuration(minutes: number, locale: Locale): string {
  if (!Number.isFinite(minutes)) {
    return '—'
  }
  const total = Math.max(0, Math.round(minutes))
  const hours = Math.floor(total / 60)
  const rest = total % 60
  if (hours === 0) {
    return locale === 'zh-CN' ? `${rest} 分钟` : `${rest} min`
  }
  if (rest === 0) {
    return locale === 'zh-CN' ? `${hours} 小时` : `${hours} h`
  }
  return locale === 'zh-CN' ? `${hours} 小时 ${rest} 分钟` : `${hours} h ${rest} min`
}

/**
 * Absolute URL for a share token, used by the copy button.
 *
 * The organiser's referral code rides along so that opening the link and signing
 * up attributes the account to whoever handed the link out. Without a code the
 * plain link is returned: an invalid `?ref=` would be worse than none.
 */
export function shareUrl(shareToken: string, referralCode?: string | null): string {
  const origin = typeof window === 'undefined' ? '' : window.location.origin
  const base = `${origin}/e/${shareToken}`
  return referralCode ? `${base}?ref=${encodeURIComponent(referralCode)}` : base
}

/**
 * Absolute site promotion URL carrying the caller's referral code.
 *
 * The landing page is the home page; no dedicated promotion page exists.
 */
export function promotionUrl(referralCode?: string | null): string {
  const origin = typeof window === 'undefined' ? '' : window.location.origin
  return referralCode ? `${origin}/?ref=${encodeURIComponent(referralCode)}` : `${origin}/`
}
